/**
 * Resolves which book path to load for SSR and client hydration, and whether that path should be
 * mirrored in the `book` URL query when syncing filter state.
 *
 * Contract:
 * - URL `book` wins when it is a non-empty string starting with `/` (same rule as legacy normalization).
 * - Otherwise, if `site.files` defines `BasefileFunction.DEFAULT_BOOK` with a non-empty path, that path
 *   is used (leading `/` added when missing so lookup matches root-relative book paths).
 * - `bookSerializeToUrl` is true only when the effective path came from an explicit valid `book` query.
 *   When the effective path is only the configured default, it is false so RouteChangeListener / AppLink
 *   do not write `book` into the URL.
 */
import { BasefileFunction, Site } from "../models/site.model";
import { getBasefilePathByFunction } from "./basefile.utils";

export type EffectiveBookResolution = {
    /** Path to use for book metadata/HTML lookup (root-relative when possible). Empty string means no book. */
    effectiveBookPath: string;
    /** True iff the effective path originates from a valid explicit `book` query param. */
    bookSerializeToUrl: boolean;
};

/** Normalize raw `book` query: valid only if non-empty after trim and starts with `/`. */
export function normalizeBookQueryParam(raw: string | null | undefined): string {
    if (raw == null || typeof raw !== "string") return "";
    const t = raw.trim();
    return t.startsWith("/") ? t : "";
}

function normalizeConfiguredDefaultPath(raw: string | null): string {
    if (!raw) return "";
    const t = raw.trim();
    if (!t) return "";
    return t.startsWith("/") ? t : `/${t}`;
}

/**
 * @param rawBookQuery — value from `book` query (e.g. `req.query.book` as string), not URL-encoded twice
 */
export function resolveEffectiveBookFromQuery(
    rawBookQuery: string | null | undefined,
    site: Site | null | undefined,
): EffectiveBookResolution {
    const urlBook = normalizeBookQueryParam(rawBookQuery ?? undefined);
    if (urlBook) {
        return { effectiveBookPath: urlBook, bookSerializeToUrl: true };
    }
    const defaultRaw = getBasefilePathByFunction(site, BasefileFunction.DEFAULT_BOOK);
    const defaultPath = normalizeConfiguredDefaultPath(defaultRaw);
    if (defaultPath) {
        return { effectiveBookPath: defaultPath, bookSerializeToUrl: false };
    }
    return { effectiveBookPath: "", bookSerializeToUrl: false };
}
